/**
 * Roly obchodník / rozpočtár (KOLO 231) — čo smie prihlásený upravovať.
 *
 * Práva počíta **server** (`services/opravnenia`) a posiela ich v
 * `GET /api/auth/me`; frontend pravidlá neopakuje, len podľa nich skryje
 * alebo zamkne ovládanie. Server zmenu bez práva odmietne tak či tak (403).
 *
 * | | Nábytok | Tovar | Podmienky ponuky | Výroba a jej cenník | Cenník tovaru |
 * |---|---|---|---|---|---|
 * | Rozpočtár | všetko okrem podmienok | číta | číta | upravuje | číta |
 * | Obchodník | len ceny | všetko | upravuje | číta | upravuje |
 * | Admin, bez prihlásenia | všetko | všetko | všetko | všetko | všetko |
 */
import type { components } from "@/api/types.gen";

export type Prava = components["schemas"]["PravaInfo"];

/** Bez prihlásenia, admin a kým `/me` nedobehne — rozhoduje server. */
export const PLNE_PRAVA: Prava = {
  nabytok: "vsetko",
  tovar: "vsetko",
  podmienky: true,
  cennik_vyroby: true,
  cennik_tovaru: true,
  nastavenia_vyroby: true,
  rola: null,
};

export function pravaZMe(me: { prava?: Prava | null } | null | undefined): Prava {
  return me?.prava ?? PLNE_PRAVA;
}

/**
 * Ako sa dá pracovať s variantou — `plny`, `len_ceny` (obchodník
 * v nábytku: zľavy, ručná cena dielca, zisk, koeficient) alebo `citanie`.
 */
export type RezimVarianty = "plny" | "len_ceny" | "citanie";

export function rezimVarianty(prava: Prava, jeTovar: boolean): RezimVarianty {
  const r = jeTovar ? prava.tovar : prava.nabytok;
  return r === "vsetko" ? "plny" : r === "ceny" ? "len_ceny" : "citanie";
}

/** Vety ako na serveri — do tooltipov zamknutého ovládania. */
export const VETA = {
  nabytok:
    "Nábytok upravuje rozpočtár — ty v ňom meníš len cenu (zľavu, ručnú cenu dielca, zisk, koeficient).",
  tovar: "Tovar upravuje obchodník — rozpočtár ho vidí len na čítanie.",
  podmienky:
    "Koeficient a zľavu ponuky nastavuje obchodník — rozpočtár ich vidí len na čítanie.",
  vyroba: "Toto patrí výrobe a upravuje to rozpočtár — obchodník to vidí len na čítanie.",
  cennikVyroby: "Cenník výroby upravuje rozpočtár — obchodník mení len tovar.",
  cennikTovaru: "Cenník tovaru upravuje obchodník — rozpočtár ho vidí len na čítanie.",
  aktivnyTovar: "Ktorý tovar ide do ceny zákazky, vyberá obchodník.",
} as const;

/** Prečo nesmie meniť štruktúru varianty — `null` = smie. */
export function zamokVarianty(prava: Prava, jeTovar: boolean): string | null {
  if (rezimVarianty(prava, jeTovar) === "plny") return null;
  return jeTovar ? VETA.tovar : VETA.nabytok;
}

/** Smie zapisovať do cenníka položky (tovar / výroba)? */
export function smieCennik(prava: Prava, tovar: boolean): boolean {
  return tovar ? prava.cennik_tovaru : prava.cennik_vyroby;
}

/** Názov roly do UI. */
export function nazovRoly(rola: string | null | undefined, jeAdmin = false): string {
  if (jeAdmin) return "Admin";
  return rola === "obchodnik" ? "Obchodník" : "Rozpočtár";
}
